import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { PERSIAN_LATTER_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import { useCountryRequest } from '@/common/hooks/useCountryRequest';
import { UseGetAllCounty } from '@/common/hooks/UseGetAllCounty';
import { useLicenseTypeRequest } from '@/common/hooks/useLicenseTypeRequest';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { DRIVING_LICENSE_REGEX } from '@/common/utils/rules/drivingLicenseRegex.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { BodyDriverInfoTypeForm } from '@/modules/adjusterReportModules/Insurer-information/pages/driver-owner/driverInfo/BodyDriverInfo.type';
import { PatchBodyCertificationData } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyCertification/patchBodyCertificationQuery.type';
import { patchBodyCertificationQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyCertification/patchBodyCertificationQueryOptions';
import { useMutation, useQuery } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import useDebounce from '@/common/hooks/useDebounce';
import { LicenseType } from '@/modules/adjusterReportModules/IncidentParticipants/pages/damaged/propertyDamage/driver-owner/driverInfo/driverInfo.type';

const BodyCertificateInformation = () => {
  const methods = useFormContext<BodyDriverInfoTypeForm>();
  const [countryName, setCountryName] = React.useState<string>('');
  const [cityName, setCityName] = React.useState<string>('');
  const navigate = useNavigate();
  const { id } = useParams();

  const debounce = useDebounce();

  const selectedCountry = methods.watch(
    'certificate_information.license_issue_location_country',
  );
  const { countryQuery: defaultCountryQuery } = useCountryRequest('ایران');

  const hasCertificate = methods.watch('certificate_information.license_type');
  const isNoLicense = hasCertificate?.value === 13;
  const { licenseTypeQuery } = useLicenseTypeRequest();
  const { mutate: patchCertification, isPending: loading } = useMutation({
    ...patchBodyCertificationQueryOptions(),
    onSuccess: () => {
      navigate(
        ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
          id: id!,
        }).DRIVER_OWNER_INFORMATION.driverContact,
        { replace: true },
      );
    },
  });
  const { countryQuery } = useCountryRequest(countryName);
  const { data: countyList } = useQuery(UseGetAllCounty(cityName));

  const searchCity = (value: string) =>
    debounce(() => {
      setCityName(value);
    }, 1000);

  const searchCountry = (value: string) =>
    debounce(() => {
      setCountryName(value);
    }, 1000);

  const onSubmit = (formData: BodyDriverInfoTypeForm) => {
    const { certificate_information } = formData;
    const isNoLicense = hasCertificate?.value === 13;
    const baseData: PatchBodyCertificationData = {
      license_type: +certificate_information?.license_type?.value || 0,
      license_issue_date: null,
      license_number: null,
      license_issue_location_country: null,
      license_issue_location_city: null,
      iran_license_issue_location_city: null,
      driver_license_is_extension: false,
    };

    const certificateData: PatchBodyCertificationData = isNoLicense
      ? baseData
      : {
          ...baseData,
          license_issue_date: convertPersianToGregorian(
            certificate_information?.license_issue_date?.toString() || '',
          ),
          license_number: certificate_information?.license_number || null,
          license_issue_location_country:
            certificate_information?.license_issue_location_country?.value || 0,
          license_issue_location_city:
            certificate_information?.license_issue_location_city || null,
          driver_license_is_extension:
            certificate_information?.driver_license_is_extension ?? false,
          iran_license_issue_location_city:
            selectedCountry?.value === 103
              ? certificate_information?.iran_license_issue_location_city
                  ?.value || null
              : null,
        };

    patchCertification({
      data: certificateData,
      id: +id!,
    });
  };
  const previousStep = () => {
    navigate(
      ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
        id: id!,
      }).DRIVER_OWNER_INFORMATION.driver,
      {
        replace: true,
      },
    );
  };

  const handleLicenseTypeChange = (option: LicenseType) => {
    if (option.value !== 13) return;

    methods.setValue('certificate_information.license_number', '');

    methods.setValue('certificate_information.license_issue_date', null);

    methods.setValue(
      'certificate_information.license_issue_location_country',
      null,
    );

    methods.setValue(
      'certificate_information.iran_license_issue_location_city',
      null,
    );

    methods.setValue('certificate_information.license_issue_location_city', '');
  };

  return (
    <Flex direction="column" gap={8}>
      <Box>
        <ModalInput
          name="certificate_information.license_type"
          label="نوع گواهی نامه"
          placeholder="انتخاب کنید"
          rules={{ required: requiredText }}
          options={licenseTypeQuery.data || []}
          defaultValue=""
          onChange={handleLicenseTypeChange}
        />
      </Box>
      <Box>
        <TextInput
          label="شماره گواهینامه"
          name="certificate_information.license_number"
          placeholder="شماره گواهینامه را وارد کنید"
          rules={{
            ...(isNoLicense ? {} : DRIVING_LICENSE_REGEX),
            required: isNoLicense ? false : requiredText,
          }}
          defaultValue=""
          disabled={isNoLicense}
          max={10}
          noCharInput
        />
      </Box>
      {!isNoLicense && (
        <Box>
          <DateInput
            label="تاریخ صدور گواهینامه"
            name="certificate_information.license_issue_date"
            buttonText="انتخاب تاریخ"
            modalLabel="انتخاب تاریخ صدور گواهینامه"
            defaultValue=""
            rules={{
              required: isNoLicense
                ? false
                : { message: requiredText, value: true },
              validate: validateNotFutureDate,
            }}
            disabled={isNoLicense}
          />
        </Box>
      )}
      <Box>
        <ModalInput
          name="certificate_information.license_issue_location_country"
          label="کشور محل صدور گواهینامه"
          placeholder="انتخاب کنید"
          options={countryQuery.data || []}
          rules={{ required: isNoLicense ? false : requiredText }}
          defaultValue={defaultCountryQuery?.data?.[0]}
          disabled={isNoLicense}
          searchOptions={{
            onChange: searchCountry,
            placeholder: 'جستجو کشور',
          }}
        />
      </Box>
      {selectedCountry?.value === 103 && (
        <Box>
          <ModalInput
            name="certificate_information.iran_license_issue_location_city"
            label="شهر محل صدور گواهینامه"
            placeholder="انتخاب کنید"
            options={countyList || []}
            defaultValue=""
            rules={{ required: isNoLicense ? false : requiredText }}
            disabled={isNoLicense}
            searchOptions={{
              onChange: searchCity,
              placeholder: 'جستجو شهر',
            }}
          />
        </Box>
      )}
      {selectedCountry?.value !== 103 && (
        <Box>
          <TextInput
            label="شهر محل صدور گواهینامه"
            name={
              selectedCountry?.value === 103
                ? 'certificate_information.iran_license_issue_location_city'
                : 'certificate_information.license_issue_location_city'
            }
            placeholder="شهر محل صدور گواهینامه را وارد کنید"
            rules={{
              ...PERSIAN_LATTER_RULE,
              required: isNoLicense ? false : requiredText,
            }}
            defaultValue=""
            disabled={isNoLicense}
          />
        </Box>
      )}
      {!isNoLicense && (
        <Box>
          <RadioGroup
            wrapperClassName={css({ mt: '2rem' })}
            className={css({ gap: '2rem' })}
            title="آیا گواهینامه تمدیدی است ؟"
            name="certificate_information.driver_license_is_extension"
            options={[
              { label: 'بله', value: true },
              { label: 'خیر', value: false },
            ]}
            renderRadioButton={(Component) => <Box>{Component}</Box>}
          />
        </Box>
      )}
      <Flex w="100%" gap={16} mt={'2rem'}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            methods.clearErrors();
            methods.trigger(['personal_information']);
            previousStep();
          }}
        >
          مرحله قبل
        </Button>{' '}
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit, () => {})}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Flex>
    </Flex>
  );
};
export default BodyCertificateInformation;
